<template>
  <div class="form-designer" tabindex="0">
    <!-- 顶部栏 -->
    <div class="designer-header">
      <div class="header-left">
        <button class="btn-back" @click="goBack"><i class="fas fa-arrow-left"></i> 返回</button>
        <div class="header-title">
          <span class="title-text">{{ editingProcessName }}</span>
          <span class="title-sub">表单设计</span>
        </div>
      </div>
      <div class="header-center">
        <!-- 表单选择 Tab -->
        <div class="form-tabs">
          <button
            v-for="(f, i) in formList"
            :key="f._key"
            class="ftab"
            :class="{ active: i === activeFormIndex }"
            @click="switchForm(i)"
            :title="f.formName"
          >
            <template v-if="editingTabIndex === i">
              <input
                class="ftab-edit-input"
                v-model="editingTabName"
                @blur="finishRenameTab(i)"
                @keydown.enter="finishRenameTab(i)"
                @keydown.escape="cancelRenameTab"
                @click.stop
                ref="tabNameInput"
              />
            </template>
            <span v-else class="ftab-label" @dblclick.stop="startRenameTab(i, f.formName)">{{ f.formName }}</span>
            <span class="ftab-badge" v-if="f.formType === '2'">新</span>
            <span class="ftab-badge old" v-else>旧</span>
            <button class="ftab-copy" @click.stop="copyForm(i)" title="复制此表单"><i class="fas fa-copy"></i></button>
            <button class="ftab-close" @click.stop="closeForm(i)" v-if="formList.length > 1">&times;</button>
          </button>
          <button class="ftab ftab-add" @click.stop="showCreateMenu = !showCreateMenu" title="新建表单">
            <i class="fas fa-plus"></i>
          </button>
        </div>
        <!-- 新建菜单（移到 form-tabs 外面避免被 overflow 裁剪） -->
        <div v-if="showCreateMenu" class="create-menu">
          <div class="create-menu-item" @click="createNewDesignerForm">新设计器表单</div>
        </div>
      </div>
      <div class="header-right">
        <button class="btn-save" @click="handleSaveForm"><i class="fas fa-save"></i> 保存</button>
      </div>
    </div>

    <!-- 主体 -->
    <div class="designer-body" v-if="activeForm">
      <!-- 左侧：新设计器 设计模式时才显示控件面板 -->
      <ControlPalette v-if="isNewDesigner && newDesignerMode === 'design'"
        @dragStart="onDragStart"
        @dragEnd="onDragEnd"
        @addControl="addControl"
      />

      <!-- 中间画布 -->
      <div class="center-panel">
        <div class="canvas-toolbar">
          <span class="canvas-label">
            <i class="fas fa-file-alt"></i> {{ activeForm.formName }}
          </span>
          <div class="canvas-actions">
            <template v-if="isNewDesigner">
              <!-- 设计/预览切换 Tab（参考经典设计器） -->
              <div class="old-form-tabs">
                <button class="oft-btn" :class="{ active: newDesignerMode === 'design' }" @click="switchToDesignMode">设计</button>
                <button class="oft-btn" :class="{ active: newDesignerMode === 'preview' }" @click="switchToPreviewMode">预览</button>
              </div>
              <span class="menu-sep"></span>
              <button class="tool-btn" @click="toggleGrid" :class="{ active: showGrid }" title="显示网格" v-if="newDesignerMode === 'design'"><i class="fas fa-th"></i></button>
              <button class="tool-btn" @click="undo" title="撤销" v-if="newDesignerMode === 'design'"><i class="fas fa-undo"></i></button>
              <button class="tool-btn" @click="redo" title="重做" v-if="newDesignerMode === 'design'"><i class="fas fa-redo"></i></button>
              <button class="tool-btn" @click="clearSelection" v-if="newDesignerMode === 'design'"><i class="fas fa-times"></i> 取消</button>
            </template>
          </div>
        </div>

        <!-- 新设计器 - 设计模式 -->
        <div v-if="isNewDesigner && newDesignerMode === 'design'" class="canvas-wrapper" :class="{ 'show-grid': showGrid }" @dragover.prevent @drop.prevent="onDrop" @keydown.ctrl.z.prevent="undo" @keydown.ctrl.y.prevent="redo" @keydown.delete.prevent="deleteSelected" tabindex="0">
          <div v-if="!controls.length" class="canvas-empty">
            <i class="fas fa-mouse-pointer"></i>
            <p>从左侧点击或拖拽控件到此处</p>
          </div>
          <div v-else class="canvas-grid">
            <div
              v-for="ctrl in controls"
              :key="ctrl.id"
              class="grid-cell"
              :class="{ selected: ctrl.id === selectedControlId, dragging: dragId === ctrl.id }"
              :style="cellStyle(ctrl)"
              draggable="true"
              @click.stop="selectControl(ctrl.id)"
              @dragstart.stop="onCellDragStart($event, ctrl.id)"
              @dragover.stop.prevent="onCellDragOver($event, ctrl.id)"
              @drop.stop.prevent="onCellDrop(ctrl.id)"
              @dragend.stop="onCellDragEnd"
            >
              <ControlRenderer :control="ctrl" :selected-id="selectedControlId || ''" @select="selectControl" />
              <div v-if="ctrl.id === selectedControlId" class="cell-tools">
                <button class="t-btn" @click.stop="moveControl(ctrl.id, 'up')" title="上移"><i class="fas fa-arrow-up"></i></button>
                <button class="t-btn" @click.stop="duplicateControl(ctrl.id)" title="复制"><i class="fas fa-copy"></i></button>
                <button class="t-btn" @click.stop="moveControl(ctrl.id, 'down')" title="下移"><i class="fas fa-arrow-down"></i></button>
                <button class="t-btn del" @click.stop="removeControl(ctrl.id)" title="删除"><i class="fas fa-trash"></i></button>
              </div>
            </div>
          </div>
        </div>

        <!-- 新设计器 - 预览模式 -->
        <div v-else-if="isNewDesigner && newDesignerMode === 'preview'" class="new-form-preview">
          <div v-if="!controls.length" class="preview-empty">
            <i class="fas fa-file-alt"></i>
            <p>暂无表单控件，请切换到"设计"模式添加控件</p>
          </div>
          <div v-else class="preview-form-body">
            <div
              v-for="ctrl in controls"
              :key="ctrl.id"
              class="preview-cell"
              :style="cellStyle(ctrl)"
            >
              <ControlRenderer :control="ctrl" selected-id="" />
            </div>
          </div>
        </div>

        <!-- 旧系统表单（仅预览，不可编辑内容） -->
        <div v-else class="old-form-viewer">
          <div class="old-form-toolbar">
            <span class="old-form-hint"><i class="fas fa-info-circle"></i> 经典设计器表单，仅支持预览和改名。请在经典设计器中编辑内容。</span>
          </div>
          <div class="old-form-preview">
            <iframe :srcdoc="previewHtmlContent" class="preview-iframe" sandbox="allow-scripts allow-same-origin allow-forms"></iframe>
          </div>
        </div>
      </div>

      <!-- 右侧属性面板（仅新设计器 设计模式） -->
      <PropertyPanel v-if="isNewDesigner && newDesignerMode === 'design'"
        :control="selectedControl"
        @update="onPropUpdate"
        @delete="removeSelect"
        @duplicate="dupSelect"
      />
    </div>

    <div v-else class="designer-loading"><i class="fas fa-spinner fa-spin"></i> 加载中...</div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue'
import ControlPalette from './FormDesigner/ControlPalette.vue'
import ControlRenderer from './FormDesigner/ControlRenderer.vue'
import PropertyPanel from './FormDesigner/PropertyPanel.vue'
import { controlRegistry } from './FormDesigner/composables/useFormState.js'

import { getFormById, saveForm, getFormsByFlow } from '../../api/form'

// ===== 表单列表 =====
const formList = reactive([])          // [{ _key, guid, formName, formType, formContent, jsContent, controls }]
const activeFormIndex = ref(0)
const activeForm = computed(() => formList[activeFormIndex.value] || null)
const isNewDesigner = computed(() => activeForm.value?.formType === '2')

// 新设计器模式：preview（默认预览）/ design（设计）
const newDesignerMode = ref('preview')
function switchToDesignMode() { newDesignerMode.value = 'design' }
function switchToPreviewMode() { newDesignerMode.value = 'preview' }

// ===== 预览面板 =====
const previewHtmlContent = computed(() => {
  if (!activeForm.value || isNewDesigner.value) return ''
  const js = activeForm.value.jsContent || ''
  const html = activeForm.value.formContent || ''
  return '<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:sans-serif;padding:20px;}</style></head><body>' + html + '<script>' + js + '<\/script></body></html>'
})

// 新设计器相关
const showCreateMenu = ref(false)
const showGrid = ref(true)
const selectedControlId = ref(null)
const dragId = ref(null)
const dragOverId = ref(null)
const controls = computed(() => activeForm.value?.controls || [])
const selectedControl = computed(() => controls.value.find(c => c.id === selectedControlId.value) || null)

// Tab 重命名
const editingTabIndex = ref(-1)
const editingTabName = ref('')
const tabNameInput = ref(null)

function startRenameTab(index, currentName) {
  editingTabIndex.value = index
  editingTabName.value = currentName
  showCreateMenu.value = false
  // 等 DOM 更新后聚焦输入框
  nextTick(() => {
    const inputs = document.querySelectorAll('.ftab-edit-input')
    if (inputs.length > 0) inputs[inputs.length - 1].focus()
    if (inputs.length > 0) inputs[inputs.length - 1].select()
  })
}

function finishRenameTab(index) {
  const newName = editingTabName.value.trim()
  if (newName && formList[index]) {
    formList[index].formName = newName
    formList[index]._dirty = true
  }
  editingTabIndex.value = -1
  editingTabName.value = ''
}

function cancelRenameTab() {
  editingTabIndex.value = -1
  editingTabName.value = ''
}

const editingProcessName = computed(() => {
  const p = window.currentEditingProcess
  return p ? p.name : '新建表单'
})

// 控件ID计数器
let _nextCid = 1
function genCtrlId() { return 'ctrl_' + (Date.now() % 100000) + '_' + (_nextCid++) }

// ===== 控件操作 =====
function addControl(type) {
  if (!activeForm.value || !isNewDesigner.value) return
  const reg = controlRegistry[type]
  if (!reg) return
  const ctrl = { id: genCtrlId(), type, props: { ...reg.defaultProps } }
  activeForm.value.controls.push(ctrl)
  selectedControlId.value = ctrl.id
  activeForm.value._dirty = true
}
function selectControl(id) {
  selectedControlId.value = id
  // 让画布获得焦点，以便 Delete 键生效
  const wrapper = document.querySelector('.canvas-wrapper')
  if (wrapper) wrapper.focus()
}
function clearSelection() { selectedControlId.value = null }
function onPropUpdate() { if (activeForm.value) activeForm.value._dirty = true }
function duplicateControl(id) {
  const idx = controls.value.findIndex(c => c.id === id)
  if (idx === -1) return
  const orig = controls.value[idx]
  const copy = { id: genCtrlId(), type: orig.type, props: { ...orig.props } }
  activeForm.value.controls.splice(idx + 1, 0, copy)
  selectedControlId.value = copy.id
  activeForm.value._dirty = true
}
function dupSelect() { if (selectedControlId.value) duplicateControl(selectedControlId.value) }
function removeSelect() { if (selectedControlId.value) removeControl(selectedControlId.value) }
function removeControl(id) {
  const idx = controls.value.findIndex(c => c.id === id)
  if (idx === -1) return
  if (selectedControlId.value === id) selectedControlId.value = null
  activeForm.value.controls.splice(idx, 1)
  activeForm.value._dirty = true
}
function moveControl(id, dir) {
  const idx = controls.value.findIndex(c => c.id === id)
  if (idx === -1) return
  const target = dir === 'up' ? idx - 1 : idx + 1
  if (target < 0 || target >= controls.value.length) return
  const tmp = controls.value[idx]
  activeForm.value.controls.splice(idx, 1)
  activeForm.value.controls.splice(target, 0, tmp)
  activeForm.value._dirty = true
}
function deleteSelected() { removeSelect() }
function cellStyle(ctrl) {
  const props = ctrl.props || {}
  const cs = props.colSpan || 12
  return { flex: '0 0 ' + (cs / 12 * 100).toFixed(2) + '%', maxWidth: (cs / 12 * 100).toFixed(2) + '%' }
}

function onDragStart(type) { document.body.style.cursor = 'grabbing'; window.draggingControlType = type }
function onDragEnd() { document.body.style.cursor = ''; window.draggingControlType = null }
function onDrop() {
  const type = window.draggingControlType
  if (type) { addControl(type); window.draggingControlType = null }
}

// ===== 控件拖拽排序 =====
function onCellDragStart(e, id) {
  dragId.value = id
  dragOverId.value = null
  e.dataTransfer.effectAllowed = 'move'
  e.dataTransfer.setData('text/plain', id)
}
function onCellDragOver(e, id) {
  if (dragId.value && dragId.value !== id) {
    dragOverId.value = id
  }
}
function onCellDrop(targetId) {
  if (!dragId.value || dragId.value === targetId) return
  const controls = activeForm.value.controls
  const fromIdx = controls.findIndex(c => c.id === dragId.value)
  const toIdx = controls.findIndex(c => c.id === targetId)
  if (fromIdx === -1 || toIdx === -1) return
  const [moved] = controls.splice(fromIdx, 1)
  controls.splice(toIdx, 0, moved)
  activeForm.value._dirty = true
}
function onCellDragEnd() {
  dragId.value = null
  dragOverId.value = null
}

// 简易撤销/重做
const undoStack = ref([])
const redoStack = ref([])
function snapshotControls() {
  if (!activeForm.value || !isNewDesigner.value) return
  return JSON.parse(JSON.stringify(activeForm.value.controls))
}
function pushHistory() {
  undoStack.value.push(snapshotControls())
  redoStack.value = []
}
function undo() {
  if (!activeForm.value || !isNewDesigner.value || !undoStack.value.length) return
  redoStack.value.push(snapshotControls())
  activeForm.value.controls = undoStack.value.pop()
}
function redo() {
  if (!activeForm.value || !isNewDesigner.value || !redoStack.value.length) return
  undoStack.value.push(snapshotControls())
  activeForm.value.controls = redoStack.value.pop()
}
// watch控制变更记录历史
watch(() => activeForm.value?.controls?.length, () => { if (activeForm.value?._dirty) pushHistory() })

// ===== 表单切换 =====
let _formKey = 0
function switchForm(index) {
  showCreateMenu.value = false
  if (index === activeFormIndex.value) return
  saveCurrentControls()
  activeFormIndex.value = index
  if (activeForm.value && isNewDesigner.value) {
    loadCurrentControls()
    // 切换表单时默认预览模式
    newDesignerMode.value = 'preview'
  }
}

function saveCurrentControls() {
  const f = formList[activeFormIndex.value]
  if (f && f.formType === '2') {
    f.formContent = JSON.stringify(f.controls || [])
  }
}
function loadCurrentControls() {
  const f = activeForm.value
  if (!f || f.formType !== '2') return
  try {
    f.controls = JSON.parse(f.formContent || '[]')
  } catch { f.controls = [] }
  selectedControlId.value = null
  undoStack.value = [snapshotControls()]
  redoStack.value = []
}

function closeForm(index) {
  if (formList.length <= 1) return
  formList.splice(index, 1)
  if (activeFormIndex.value >= formList.length) activeFormIndex.value = formList.length - 1
}

function copyForm(index) {
  const src = formList[index]
  if (!src) return
  // 序列化再反序列化，确保深拷贝
  const baseName = src.formName + ' - 副本'
  const copyName = generateUniqueName(baseName)
  const copy = JSON.parse(JSON.stringify({
    formName: copyName,
    formType: src.formType,
    formContent: src.formType === '2' ? JSON.stringify(src.controls || []) : (src.formContent || ''),
    jsContent: src.jsContent || '',
    controls: src.formType === '2' ? (src.controls || []).map(c => ({ ...c, id: genCtrlId() })) : [],
    guid: '',
    _dirty: true,
    _key: ++_formKey
  }))
  formList.splice(index + 1, 0, copy)
  // 切换到新表单
  saveCurrentControls()
  activeFormIndex.value = index + 1
  loadCurrentControls()
}

// 生成不重复的表单名称
function generateUniqueName(baseName) {
  const existingNames = new Set(formList.map(f => f.formName))
  if (!existingNames.has(baseName)) return baseName
  let counter = 2
  let candidate
  do {
    candidate = baseName + counter
    counter++
  } while (existingNames.has(candidate))
  return candidate
}

function createNewDesignerForm() {
  console.log('[FormDesigner] 创建新设计器表单')
  showCreateMenu.value = false
  saveCurrentControls()
  const name = generateUniqueName('新建表单')
  formList.push({
    _key: ++_formKey,
    guid: '',
    formName: name,
    formType: '2',
    formContent: '[]',
    jsContent: '',
    controls: [],
    _dirty: true
  })
  activeFormIndex.value = formList.length - 1
  loadCurrentControls()
  // 新创建的表单默认进入设计模式
  newDesignerMode.value = 'design'
  console.log('[FormDesigner] 新表单已创建，当前表单数:', formList.length)
}

// ===== 保存 =====
async function handleSaveForm() {
  const f = activeForm.value
  if (!f) return
  try {
    if (f.formType === '2') {
      f.formContent = JSON.stringify(f.controls || [])
    }
    const p = window.currentEditingProcess
    const payload = {
      Guid: f.guid || '',
      FlowGuid: p?.guid || '',
      FormName: f.formName,
      FormContent: f.formContent || '',
      JSContent: f.jsContent || '',
      FormType: f.formType
    }
    const res = await saveForm(payload)
    const data = extractData(res)
    if (!f.guid && data?.guid) {
      f.guid = data.guid || data.Guid || ''
    }
    f._dirty = false
    window.$message.success('保存成功！')
  } catch (e) {
    window.$message.error('保存失败: ' + (e.message || e))
  }
}

function extractData(res) {
  const d = res?.data || res?.Data || res
  return d?.data || d?.Data || d?.DataList || d
}

// ===== 加载表单列表 =====
async function loadForms() {
  const p = window.currentEditingProcess
  if (!p || !p.guid) return
  try {
    const res = await getFormsByFlow(p.guid)
    const list = extractData(res)
    if (Array.isArray(list) && list.length > 0) {
      formList.length = 0
      list.forEach(f => {
        const formType = String(f.formType ?? f.FormType ?? '2')
        const isNew = formType === '2'
        const formContent = f.formContent ?? f.FormContent ?? ''
        const jsContent = f.jsContent ?? f.JSContent ?? ''
        let controls = []
        if (isNew) {
          try { controls = JSON.parse(formContent || '[]') } catch { controls = [] }
        }
        formList.push({
          _key: ++_formKey,
          guid: f.guid ?? f.Guid ?? '',
          formName: f.formName ?? f.FormName ?? '未命名表单',
          formType: formType,
          formContent: formContent,
          jsContent: jsContent,
          controls: controls,
          _dirty: false
        })
      })
      activeFormIndex.value = 0
      if (isNewDesigner.value) {
        loadCurrentControls()
        // 加载已有表单时默认预览模式
        newDesignerMode.value = 'preview'
      }
      console.log('[FormDesigner] 已加载 ' + formList.length + ' 个表单')
    } else {
      createNewDesignerForm()
    }
  } catch (e) {
    console.warn('[FormDesigner] 加载表单列表失败:', e)
    createNewDesignerForm()
  }
}

function goBack() { window.dispatchEvent(new Event('navigateToProcessManagement')) }
function toggleGrid() { showGrid.value = !showGrid.value }

// 点击外部关闭新建菜单
onMounted(() => {
  loadForms()
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.ftab-add') && !e.target.closest('.create-menu')) {
      showCreateMenu.value = false
    }
  })
})
</script>

<style scoped>
.form-designer { display: flex; flex-direction: column; height: calc(100vh - 110px); background: #f0fdf4; outline: none; }
.designer-header { display: flex; align-items: center; padding: 0 15px; height: 50px; background: #fff; border-bottom: 1px solid #d1fae5; flex-shrink: 0; gap: 12px; }
.header-left { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.btn-back { display: flex; align-items: center; gap: 4px; padding: 5px 10px; border: 1px solid #d1fae5; border-radius: 4px; background: #fff; font-size: 12px; color: #059669; cursor: pointer; }
.btn-back:hover { background: #ecfdf5; }
.title-text { font-size: 14px; font-weight: 600; color: #064e3b; white-space: nowrap; }
.title-sub { font-size: 10px; color: #6ee7b7; margin-left: 4px; }

/* 表单 tabs */
.header-center { flex: 1; display: flex; min-width: 0; position: relative; }
.form-tabs { display: flex; align-items: center; gap: 2px; overflow-x: auto; flex: 1; min-width: 0; position: relative; }
.form-tabs::-webkit-scrollbar { height: 3px; }
.form-tabs::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
.ftab { display: flex; align-items: center; gap: 4px; padding: 4px 10px; border: 1px solid #e5e7eb; border-radius: 4px 4px 0 0; background: #f3f4f6; font-size: 12px; color: #6b7280; cursor: pointer; white-space: nowrap; flex-shrink: 0; position: relative; }
.ftab:hover { background: #e5e7eb; }
.ftab.active { background: #fff; color: #10b981; font-weight: 500; border-bottom-color: #fff; }
.ftab-label { max-width: 120px; overflow: hidden; text-overflow: ellipsis; }
.ftab-badge { font-size: 9px; padding: 1px 4px; border-radius: 3px; background: #10b981; color: #fff; }
.ftab-badge.old { background: #f59e0b; }
.ftab-close { width: 16px; height: 16px; border: none; background: transparent; font-size: 12px; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; border-radius: 2px; line-height: 1; margin-left: 2px; }
.ftab-close:hover { background: #ef4444; color: #fff; }
.ftab-copy { width: 16px; height: 16px; border: none; background: transparent; font-size: 10px; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; border-radius: 2px; line-height: 1; margin-left: 2px; }
.ftab-copy:hover { background: #3b82f6; color: #fff; }
.ftab-add { padding: 4px 8px; border: 1px dashed #d1d5db; background: transparent; font-size: 12px; color: #9ca3af; cursor: pointer; border-radius: 4px; }
.ftab-add:hover { border-color: #10b981; color: #10b981; background: #ecfdf5; }
.ftab-edit-input { width: 80px; padding: 1px 4px; border: 1px solid #10b981; border-radius: 3px; font-size: 12px; outline: none; background: #fff; color: #374151; }
.create-menu { position: absolute; top: 100%; left: 0; z-index: 100; background: #fff; border: 1px solid #d1fae5; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); min-width: 180px; padding: 4px; margin-top: 4px; }
.create-menu-item { padding: 8px 14px; font-size: 13px; color: #374151; cursor: pointer; border-radius: 4px; }
.create-menu-item:hover { background: #ecfdf5; color: #10b981; }

.header-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.btn-save { display: flex; align-items: center; gap: 4px; padding: 6px 16px; border: none; border-radius: 4px; background: #10b981; font-size: 13px; color: #fff; cursor: pointer; }
.btn-save:hover { background: #059669; }

.designer-body { display: flex; flex: 1; overflow: hidden; }
.designer-loading { flex: 1; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 14px; gap: 8px; }

.center-panel { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.canvas-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 8px 15px; background: #fff; border-bottom: 1px solid #d1fae5; }
.canvas-label { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 500; color: #374151; }
.canvas-label i { color: #10b981; }
.canvas-actions { display: flex; gap: 6px; }
.tool-btn { padding: 4px 10px; border: 1px solid #e5e7eb; border-radius: 4px; background: #fff; font-size: 12px; color: #6b7280; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.tool-btn:hover { background: #ecfdf5; color: #10b981; border-color: #10b981; }
.tool-btn.active { background: #ecfdf5; color: #10b981; border-color: #10b981; }
.menu-sep { width: 1px; height: 18px; background: #d1d5db; margin: 0 4px; display: inline-block; }

.canvas-wrapper { flex: 1; padding: 20px; overflow-y: auto; }
.canvas-wrapper.show-grid { background-image: linear-gradient(to right, #d1fae5 1px, transparent 1px), linear-gradient(to bottom, #d1fae5 1px, transparent 1px); background-size: 20px 20px; }
.canvas-grid { display: flex; flex-wrap: wrap; align-items: flex-start; }
.canvas-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: #9ca3af; }
.canvas-empty i { font-size: 48px; margin-bottom: 12px; opacity: 0.4; }
.canvas-empty p { font-size: 14px; }
.grid-cell { position: relative; padding: 12px; border: 2px solid transparent; border-radius: 6px; background: #fff; box-sizing: border-box; transition: all 0.15s; margin-bottom: 2px; cursor: grab; }
.grid-cell:hover { border-color: #a7f3d0; background: #f9fdfb; }
.grid-cell.selected { border-color: #10b981; background: #f0fdf4; z-index: 2; }
.grid-cell.dragging { opacity: 0.4; border-style: dashed; }
.grid-cell.drag-over { border-color: #1ab394; border-style: dashed; background: #e6fffa; }
.cell-tools { position: absolute; top: 2px; right: 2px; display: flex; flex-direction: row; gap: 2px; z-index: 10; opacity: 0.8; }
.t-btn { width: 28px; height: 28px; border: 1px solid #d1d5db; border-radius: 4px; background: #fff; font-size: 11px; color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.t-btn:hover { background: #10b981; color: #fff; border-color: #10b981; }
.t-btn.del:hover { background: #ef4444; border-color: #ef4444; }

.old-form-viewer { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.old-form-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 6px 15px; background: #fff; border-bottom: 1px solid #d1fae5; }
.old-form-hint { font-size: 12px; color: #f59e0b; display: flex; align-items: center; gap: 6px; }
.old-form-preview { flex: 1; overflow: hidden; background: #fff; }
.preview-iframe { width: 100%; height: 100%; border: none; }

/* 新设计器预览模式 */
.new-form-preview { flex: 1; overflow-y: auto; padding: 20px; background: #fff; }
.preview-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: #9ca3af; }
.preview-empty i { font-size: 48px; margin-bottom: 12px; opacity: 0.4; }
.preview-empty p { font-size: 14px; }
.preview-form-body { display: flex; flex-wrap: wrap; align-items: flex-start; max-width: 100%; }
.preview-cell { padding: 10px 12px; box-sizing: border-box; }
</style>
